import { useState, useEffect, useRef } from 'react';
import { useOperator } from '@/lib/operator-context';
import { getFlowCounts, getFlowFunnel } from '@/lib/operator-api/flows-client';
import type { FunnelStage } from '@/lib/operator-api/flows-client';

export function useJourneyEvents(options: {
  conversationId: string;
  enabled: boolean;
}) {
  return {
    events: [],
    isLoading: false,
    error: null,
    refetch: () => {},
  };
}

export function useFlowCounts(options: {
  flowId: string;
  enabled: boolean;
  refetchInterval?: number;
}) {
  const { selectedBusinessId } = useOperator();
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!options.enabled || !selectedBusinessId) return;

    let cancelled = false;

    async function fetchCounts() {
      setIsLoading(true);
      try {
        const data = await getFlowCounts(options.flowId, selectedBusinessId!);
        if (!cancelled) setCounts(data);
      } catch {
        // keep previous counts on error
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchCounts();

    if (options.refetchInterval) {
      intervalRef.current = setInterval(fetchCounts, options.refetchInterval);
    }

    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [options.flowId, options.enabled, options.refetchInterval, selectedBusinessId]);

  return { counts, isLoading };
}

export function useFunnelData(options: {
  flowId: string;
  enabled: boolean;
  refetchInterval?: number;
}) {
  const { selectedBusinessId } = useOperator();
  const [stages, setStages] = useState<Array<FunnelStage & { id: string; name: string }>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!options.enabled || !selectedBusinessId) return;

    let cancelled = false;

    async function fetchFunnel() {
      setIsLoading(true);
      try {
        const data = await getFlowFunnel(options.flowId, selectedBusinessId!);
        if (!cancelled) {
          setStages(data.funnel.map((s) => ({
            ...s,
            id: s.stage,
            name: s.displayName,
          })));
        }
      } catch {
        // keep previous data on error
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    fetchFunnel();

    if (options.refetchInterval) {
      intervalRef.current = setInterval(fetchFunnel, options.refetchInterval);
    }

    return () => {
      cancelled = true;
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [options.flowId, options.enabled, options.refetchInterval, selectedBusinessId]);

  return { stages, isLoading };
}
